import { useMemo } from "react";
import { X, RotateCcw, Trash2 } from "lucide-react";
import { useNotesStore } from "../stores/notes-store";

interface TrashPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export function TrashPanel({ isOpen, onClose }: TrashPanelProps) {
  const notes = useNotesStore((s) => s.notes);
  const restoreNote = useNotesStore((s) => s.restoreNote);
  const permanentlyDeleteNote = useNotesStore((s) => s.permanentlyDeleteNote);

  const deletedNotes = useMemo(
    () => notes.filter((n) => n.isDeleted).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
    [notes]
  );

  const handleEmptyTrash = () => {
    if (window.confirm("Permanently delete all items in trash? This cannot be undone.")) {
      deletedNotes.forEach((n) => permanentlyDeleteNote(n.id));
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/50"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed inset-y-0 right-0 z-50 w-full max-w-md border-l border-surface-3 bg-surface-0 shadow-xl dark:border-surface-dark-3 dark:bg-surface-dark-0">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-surface-3 px-4 py-3 dark:border-surface-dark-3">
          <h2 className="text-lg font-semibold">Trash</h2>
          <div className="flex items-center gap-2">
            {deletedNotes.length > 0 && (
              <button
                onClick={handleEmptyTrash}
                className="rounded-md px-3 py-1.5 text-xs text-red-500 transition hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                Empty Trash
              </button>
            )}
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-gray-400 transition hover:bg-surface-1 dark:hover:bg-surface-dark-1"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-auto p-4">
          {deletedNotes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center text-gray-400">
              <Trash2 className="mb-3 h-10 w-10" />
              <p className="text-sm">Trash is empty</p>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {deletedNotes.map((note) => (
                <div
                  key={note.id}
                  className="flex items-center gap-3 rounded-lg px-3 py-2 transition hover:bg-surface-1 dark:hover:bg-surface-dark-1"
                >
                  <span className="text-lg">{note.icon || "📄"}</span>
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-sm font-medium">
                      {note.title || "Untitled"}
                    </p>
                    <p className="text-xs text-gray-400">
                      Deleted {new Date(note.updatedAt).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={() => restoreNote(note.id)}
                    className="rounded-md p-1.5 text-gray-400 transition hover:bg-surface-2 hover:text-green-500 dark:hover:bg-surface-dark-2"
                    title="Restore"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(`Permanently delete "${note.title || "Untitled"}"?`)) {
                        permanentlyDeleteNote(note.id);
                      }
                    }}
                    className="rounded-md p-1.5 text-gray-400 transition hover:bg-surface-2 hover:text-red-500 dark:hover:bg-surface-dark-2"
                    title="Delete forever"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
